import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import Tesseract, { Worker } from 'tesseract.js'
import { Footer } from '@/components/molecules/front-tablet/footer'
import { getSelfCheckinUploadKey, postIdentificationVerify, debugState } from '@/apis/aipass'
import uuid from 'react-uuid'
import axios, { AxiosError } from 'axios'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import { LoaderContextCreator } from '@/contexts/loader'
import { ErrorDialog } from './error-dialog'
import { reportError } from '@/components/apps/error-boundary'
import type { UploadKey } from '@/models/file-upload'
import dayjs from 'dayjs'
import { pickUploadReservationUuid } from '@/utils/reservations'

type Props = {
  onCancel: () => void
  onShutter: (image: string, nationality: string | undefined, objectKey: string) => void
  type: 'license' | 'passport'
  side?: 'front' | 'back'
}

export const IdentityVerifyCamera: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { state } = useSelfCheckInState()
  const { setIsLoading } = useContext(LoaderContextCreator())

  const [tesseractWorker, setTesseractWorker] = useState<Worker>()
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [mediaStream, setMediaStream] = useState<MediaStream>()
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>()
  const [uploadKey, setUploadKey] = useState<UploadKey>()
  const [isShowErrorDialog, setIsShowErrorDialog] = useState(false)
  const [isMismatchCameraOrientation, setIsMismatchCameraOrientation] = useState(false)
  const [debugDetectResult, setDebugDetectResult] = useState<any>([false, false])
  const [isCameraReady, setIsCameraReady] = useState(false)

  const captureCanvasRef = useRef<HTMLCanvasElement>(null)
  const prevTopCanvasRef = useRef<HTMLCanvasElement>(null)
  const prevBottomCanvasRef = useRef<HTMLCanvasElement>(null)
  const isUploadingRef = useRef(isUploading)

  const cv = (window as any).cv
  const csParameter = {
    blurSize: 3,
    blockSize: 7,
    cValue: 7,
  }
  const passportGuideHeight = 280 * 1.5
  const passportGuideWidth = 400 * 1.5

  const videoFrameCanvasId = 'video-canvas'

  const disabledOCR = props.type !== 'passport'

  const recognize = async prevCanvas => {
    const src = cv.imread(prevCanvas)
    const gray = new cv.Mat()
    const blurred = new cv.Mat()
    const adaptive = new cv.Mat()
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY)
    const ksize = new cv.Size(csParameter.blurSize, csParameter.blurSize)
    cv.GaussianBlur(gray, blurred, ksize, 0)
    cv.adaptiveThreshold(blurred, adaptive, 255, cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY, csParameter.blockSize, csParameter.cValue)
    cv.imshow(prevCanvas, adaptive)
    src.delete()
    gray.delete()
    blurred.delete()
    adaptive.delete()

    // const start = performance.now()
    const {
      data: { text },
    } = await tesseractWorker!.recognize(prevCanvas)
    const trimmedText = text.trim()
    // const end = performance.now()
    // console.log({ res: end - start, trimmedText })
    return trimmedText.replace(/[Ａ-Ｚａ-ｚ０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xfee0)).toLowerCase()
  }

  const captureAndVerifyVideoFrame = async () => {
    const prevTopCanvas = prevTopCanvasRef.current
    const prevBottomCanvas = prevBottomCanvasRef.current
    const captureCanvas = captureCanvasRef.current
    if (!prevTopCanvas || !prevBottomCanvas || !captureCanvas) {
      debugState('identification-error', { message: 'ref' })
      return
    }
    if (!snapShotVideoFrame()) {
      debugState('identification-error', { message: 'frame' })
      return
    }
    const videoFrameCanvas = getVideoFrameCanvas()
    if (!videoFrameCanvas) {
      debugState('identification-error', { message: 'canvs' })
      return
    }
    prevTopCanvas.width = videoFrameCanvas.width / 2
    prevTopCanvas.height = videoFrameCanvas.height / 4
    const ctx = prevTopCanvas.getContext('2d')
    if (!ctx) {
      debugState('identification-error', { message: 'ctx' })
      return
    }
    ctx.drawImage(
      captureCanvas,
      (captureCanvas.width - videoFrameCanvas.width) / 2,
      (captureCanvas.height - videoFrameCanvas.height) / 2,
      prevTopCanvas.width,
      prevTopCanvas.height,
      0,
      0,
      prevTopCanvas.width,
      prevTopCanvas.height,
    )

    prevBottomCanvas.width = videoFrameCanvas.width / 2
    prevBottomCanvas.height = videoFrameCanvas.height / 4
    const ctx2 = prevBottomCanvas.getContext('2d')
    if (!ctx2) {
      debugState('identification-error', { message: 'ctx2' })
      return
    }
    ctx2.drawImage(
      captureCanvas,
      (captureCanvas.width - videoFrameCanvas.width) / 2 + videoFrameCanvas.width / 2,
      (captureCanvas.height - videoFrameCanvas.height) / 2 + videoFrameCanvas.height - prevBottomCanvas.height,
      prevBottomCanvas.width,
      prevBottomCanvas.height,
      0,
      0,
      prevBottomCanvas.width,
      prevBottomCanvas.height,
    )

    const ocrResults = await Promise.all([
      recognize(prevTopCanvas).then(text => text.includes('passport')),
      recognize(prevBottomCanvas).then(text => /([<l]{3,})/.test(text)),
    ])
    setDebugDetectResult(ocrResults)
    if (ocrResults[0] || ocrResults[1]) {
      verifyCaptureImage({ isManual: false })
    }
  }

  const verifyCaptureImage = async ({ isManual }: { isManual: boolean }) => {
    if (!videoElement || !isCameraReady || isUploadingRef.current) {
      debugState('identification-error', {
        isCameraReady,
        isUploadingRef: isUploadingRef.current,
      })
      return
    }
    const captureCanvas = trimUploadImageCanvas()
    if (!captureCanvas) {
      return
    }
    try {
      if (!uploadKey) {
        throw new Error('undefined uploadKey')
      }
      setIsLoading(true)
      setIsUploading(true)
      const objectKey = `${uploadKey.allowPath}/${uuid()}.jpeg`
      const captureCanvasToBlob = await new Promise<Blob>((resolve, reject) => {
        captureCanvas.toBlob(blob => (blob ? resolve(blob) : reject('failed load image')), 'image/jpeg')
      })
      const verifyResult = await verifyIdentification(state.hotelId, {
        isManual,
        objectKey,
        file: captureCanvasToBlob,
      })
      if (verifyResult.verify || !state.setting?.accommodation.requiredIdentifyVerification) {
        const thumbnailCanvas = document.createElement('canvas')
        thumbnailCanvas.width = captureCanvas.width / 2
        thumbnailCanvas.height = captureCanvas.height / 2
        const thumbnailCtx = thumbnailCanvas.getContext('2d')
        thumbnailCtx!.drawImage(captureCanvas, 0, 0, thumbnailCanvas.width, thumbnailCanvas.height)
        props.onShutter(thumbnailCanvas.toDataURL('image/jpeg'), verifyResult.nationality, objectKey)
      } else {
        setIsShowErrorDialog(true)
        setIsUploading(false)
      }
    } catch (e) {
      reportError(e)
      setIsShowErrorDialog(true)
      props.onCancel()
    } finally {
      setIsLoading(false)
    }
  }

  const verifyIdentification = async (hotelId: string, params: { isManual: boolean; objectKey: string; file: Blob }) => {
    if (!uploadKey) {
      throw new Error('undefined uploadKey')
    }
    const form = new FormData()
    form.append('key', params.objectKey)
    form.append('policy', uploadKey.policy)
    form.append('X-Amz-Credential', uploadKey.credential)
    form.append('X-Amz-Algorithm', 'AWS4-HMAC-SHA256')
    form.append('X-Amz-Date', uploadKey.signedDate)
    form.append('X-Amz-Signature', uploadKey.signature)
    form.append('Content-Type', 'image/jpeg')
    form.append('file', params.file)
    await axios.post(uploadKey.s3url, form).catch((e: AxiosError) => {
      if (e.isAxiosError) {
        reportError(e.response?.data)
      }
      throw e
    })
    return await postIdentificationVerify({
      hotelId,
      isManual: params.isManual,
      imagePath: params.objectKey,
      idType: computedIdType(),
    })
  }

  const onClickGetPicture = async (): Promise<void> => {
    if (!snapShotVideoFrame()) {
      return
    }
    await verifyCaptureImage({ isManual: true })
  }

  const getVideoFrameCanvas = () => {
    const canvas = document.getElementById(videoFrameCanvasId)
    return canvas instanceof HTMLCanvasElement ? canvas : null
  }

  const updateCameraReadyState = useCallback(
    (targetVideoElement?: HTMLVideoElement | null) => {
      const frameCanvas = getVideoFrameCanvas()
      const currentVideoElement = targetVideoElement ?? videoElement
      const isReady = Boolean(
        currentVideoElement &&
        frameCanvas &&
        currentVideoElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
        currentVideoElement.videoWidth > 0 &&
        currentVideoElement.videoHeight > 0 &&
        frameCanvas.width > 0 &&
        frameCanvas.height > 0,
      )
      setIsCameraReady(isReady)
    },
    [videoElement],
  )

  const handleVideoLoadedMetadata = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const currentVideoElement = event.currentTarget
      setIsMismatchCameraOrientation(
        window.innerHeight === currentVideoElement.videoWidth && window.innerWidth === currentVideoElement.videoHeight,
      )
      updateCameraReadyState(currentVideoElement)
    },
    [updateCameraReadyState],
  )

  const handleVideoReadyStateChange = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      updateCameraReadyState(event.currentTarget)
    },
    [updateCameraReadyState],
  )

  const snapShotVideoFrame = () => {
    const captureCanvas = captureCanvasRef.current
    if (!videoElement || !captureCanvas) {
      return false
    }
    if (videoElement.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || videoElement.videoWidth <= 0 || videoElement.videoHeight <= 0) {
      return false
    }
    captureCanvas.width = videoElement.videoWidth
    captureCanvas.height = videoElement.videoHeight
    const captureCanvasCtx = captureCanvas.getContext('2d')
    if (!captureCanvasCtx) {
      return false
    }
    captureCanvasCtx.drawImage(videoElement, 0, 0, videoElement.videoWidth, videoElement.videoHeight)
    return true
  }

  const trimUploadImageCanvas = () => {
    const captureCanvas = captureCanvasRef.current
    const videoFrameCanvas = getVideoFrameCanvas()
    if (!captureCanvas || !videoFrameCanvas) {
      debugState('identification-error', { message: 'trimUploadImageCanvas:canvas' })
      return undefined
    }
    if (captureCanvas.width <= 0 || captureCanvas.height <= 0 || videoFrameCanvas.width <= 0 || videoFrameCanvas.height <= 0) {
      debugState('identification-error', { message: 'trimUploadImageCanvas:size' })
      return undefined
    }
    const cropRate = 0.3
    let trimX = videoFrameCanvas.width * (1 + cropRate)
    let trimY = videoFrameCanvas.height * (1 + cropRate)
    let sourceX = (captureCanvas.width - trimX) / 2
    let sourceY = (captureCanvas.height - trimY) / 2

    trimX = Math.min(trimX, captureCanvas.width)
    trimY = Math.min(trimY, captureCanvas.height)
    sourceX = Math.max(0, (captureCanvas.width - trimX) / 2)
    sourceY = Math.max(0, (captureCanvas.height - trimY) / 2)

    if (sourceX < 0 || sourceY < 0) {
      debugState('identification-error', {
        message: 'trimUploadImageCanvas:source size',
        trimX,
        trimY,
        sourceX,
        sourceY,
        captureCanvasHeight: captureCanvas.height,
        captureCanvasWidth: captureCanvas.width,
      })
      return undefined
    }
    const trimCanvas = document.createElement('canvas')
    trimCanvas.width = trimX
    trimCanvas.height = trimY
    const trimCtx = trimCanvas.getContext('2d')
    if (!trimCtx) {
      debugState('identification-error', { message: 'trimUploadImageCanvas:trimCtx' })
      return undefined
    }
    trimCtx.drawImage(captureCanvas, sourceX, sourceY, trimX, trimY, 0, 0, trimX, trimY)
    return trimCanvas
  }

  const computedIdType = (): 'drivingLicenseFront' | 'drivingLicenseBack' | 'passport' => {
    if (props.type === 'license') {
      return props.side === 'front' ? 'drivingLicenseFront' : 'drivingLicenseBack'
    } else {
      return 'passport'
    }
  }

  const videoRef = useMemo(() => {
    return async (videoNode: HTMLVideoElement | null) => {
      try {
        if (!videoNode) {
          setVideoElement(null)
          setIsCameraReady(false)
          return
        }
        if (!disabledOCR) {
          setTesseractWorker(
            await Tesseract.createWorker(undefined, undefined, {
              workerPath: '/tesseract-worker.min.js',
            }),
          )
        }

        const _stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            width: { min: 957, ideal: window.innerWidth, max: 1920 },
            height: { min: 594, ideal: window.innerHeight, max: 1080 },
            facingMode: 'user',
          },
        })

        setMediaStream(_stream)

        videoNode.srcObject = _stream
        setVideoElement(videoNode)

        const videoFrameCanvas = getVideoFrameCanvas()
        if (videoFrameCanvas !== null) {
          videoFrameCanvas.width = props.type === 'license' ? 757 : passportGuideWidth
          videoFrameCanvas.height = props.type === 'license' ? 454 : passportGuideHeight
        }
        updateCameraReadyState(videoNode)
      } catch (e: any) {
        reportError(e)
        debugState('identification-error', { message: e?.message, name: e.name })
        const isGrantedCameraPermission = await navigator?.permissions
          ?.query({ name: 'camera' as PermissionName })
          .then((permission: any) => {
            return permission?.state === 'granted'
          })
        if (!isGrantedCameraPermission) {
          alert(t('CameraNotAllowed'))
          props.onCancel()
          return
        }
        if (e instanceof DOMException && e.name === 'NotAllowedError' && isGrantedCameraPermission) {
          alert(t('PermissionReload'))
          window.location.reload()
          return
        }
        throw e
      }
    }
  }, [])

  useEffect(() => {
    isUploadingRef.current = isUploading
  }, [isUploading])

  useEffect(() => {
    if (!videoElement || disabledOCR) {
      return
    }

    const interval = setInterval(async () => {
      if (isUploadingRef.current) {
        return
      }

      captureAndVerifyVideoFrame()
    }, 2000)
    return () => clearInterval(interval)
  }, [videoElement, disabledOCR, isCameraReady])

  useEffect(() => {
    if (!videoElement) {
      return
    }

    updateCameraReadyState(videoElement)
  }, [videoElement, updateCameraReadyState])

  useEffect(() => {
    return () => {
      tesseractWorker?.terminate()
      mediaStream?.getTracks()?.forEach(track => track.stop())
    }
  }, [mediaStream])

  useEffect(() => {
    getSelfCheckinUploadKey({
      hotelId: state.hotelId,
      feature: 'checkin-image',
      reservationUuid: pickUploadReservationUuid(state.selectReservation?.selectedReservations, dayjs().format('YYYY-MM-DD')),
    }).then(setUploadKey)

    navigator?.permissions?.query({ name: 'camera' as PermissionName }).then((permission: any) => {
      debugState('identification', {
        permission: { name: permission?.name, state: permission?.state },
      })
    })
  }, [])

  useEffect(() => {
    const tesseractWorkerErrorHandler = setTimeout(() => {
      if (!tesseractWorker && !disabledOCR) {
        alert(t('PermissionReload'))
        window.location.reload()
      }
    }, 5000)
    return () => {
      clearTimeout(tesseractWorkerErrorHandler)
    }
  }, [tesseractWorker])

  return (
    <>
      {/* debug  */}
      {/* <canvas ref={prevTopCanvasRef} />
      <canvas ref={prevBottomCanvasRef} />
      <canvas ref={captureCanvasRef} />
      <div>
        <p>左上：{debugDetectResult[0] ? 'true' : 'false'}</p>
        <p>右下：{debugDetectResult[1] ? 'true' : 'false'}</p>
      </div> */}

      <canvas ref={prevTopCanvasRef} style={{ display: 'none' }} />
      <canvas ref={prevBottomCanvasRef} style={{ display: 'none' }} />
      <canvas ref={captureCanvasRef} style={{ display: 'none' }} />

      <div css={videoWrapper}>
        <video
          id="video"
          ref={videoRef}
          onLoadedMetadata={handleVideoLoadedMetadata}
          onLoadedData={handleVideoReadyStateChange}
          onCanPlay={handleVideoReadyStateChange}
          playsInline
          muted
          autoPlay
          style={{ transform: isMismatchCameraOrientation ? 'scaleX(-1) rotate(-90deg)' : 'scaleX(-1)' }}
        />
        {props.type === 'passport' ? (
          <>
            <div css={guideSupportTextStyle}>{t('Hold still for 2 sec to auto-capture')}</div>
            <div css={frameTextWrapperStyle} style={{ height: passportGuideHeight }}>
              <img
                src={require('@/static/images/passport_guide_white.svg')}
                css={videoGuideWrapper}
                height={passportGuideHeight}
                width={passportGuideWidth}
              />
            </div>
          </>
        ) : props.type === 'license' && props.side === 'front' ? (
          <div css={frameTextWrapperStyle} style={{ height: 454 }}>
            <div css={frameTextLicenseStyle}>{t('Please take a picture by aligning the surface with the frame')}</div>
          </div>
        ) : (
          <div css={frameTextWrapperStyle} style={{ height: 454 }}>
            <div css={frameTextLicenseStyle}>{t('Please take a picture by aligning the back side within the frame')}</div>
          </div>
        )}
        <canvas id={videoFrameCanvasId} css={videoframeWrapper} />

        <Footer
          left={{
            isDisabled: false,
            label: t('Back'),
            onClick: props.onCancel,
          }}
          right={{
            isDisabled: !isCameraReady || isUploading,
            label: props.type === 'passport' ? t('Manual Capture') : t('Capture'),
            onClick: () => onClickGetPicture(),
          }}
        />
      </div>
      {isShowErrorDialog && <ErrorDialog type={props.type} onClose={() => setIsShowErrorDialog(false)} />}
    </>
  )
}

const videoWrapper = css({
  width: '100vw',
  height: '100vh',
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  '*': { fontFamily: 'Noto Sans JP' },
  video: {
    minWidth: '100%',
    minHeight: '100%',
    objectFit: 'cover',
  },
})

const frameTextStyle = css({
  color: '#fff',
  fontSize: 20,
  fontWeight: 'bold',
  lineHeight: '29px',
  position: 'absolute',
  left: '50%',
  margin: 'auto',
  textAlign: 'center',
})

const frameTextLicenseStyle = css(frameTextStyle, {
  top: '6.5%',
  '-webkit-transform': 'translate(-50%,-6.5%)',
  '-ms-transform': 'translate(-50%,-6.5%)',
  transform: 'translate(-50%,-6.5%)',
})
const videoframeWrapper = css({
  borderRadius: 13,
  border: '3px solid #fff',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  '-webkit-transform': 'translate(-50%, -50%)',
  '-ms-transform': 'translate(-50%, -50%)',
  margin: 'auto',
})
const videoGuideWrapper = css({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  '-webkit-transform': 'translate(-50%, -50%)',
  '-ms-transform': 'translate(-50%, -50%)',
  margin: 'auto',
})

const frameTextWrapperStyle = css({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%,-50%)',
  margin: 'auto',
  width: '100%',
})

const guideSupportTextStyle = css({
  position: 'absolute',
  // guideHeight(280*1.5), fontSize(26), padding(32)
  top: 'calc(50% - 280px * 1.5 / 2 - 26px - 32px)',
  fontSize: 26,
  color: '#fff',
  fontWeight: 'bold',
})
